import React from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { useAuth } from './hooks/useAuth';
import { LandingPage } from './pages/LandingPage';
import { LoadingSpinner } from './components/LoadingSpinner';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { DeckLibraryPage } from './pages/DeckLibraryPage';
import { SessionsPage } from './pages/SessionsPage';
import { SessionPage } from './pages/SessionPage';
import { DataPage } from './pages/DataPage';
import { SettingsPage } from './pages/SettingsPage';
import { ProfilesPage } from './pages/ProfilesPage';
import { SavedDecksPage } from './pages/SavedDecksPage';
import { QuickLearnPage } from './pages/QuickLearnPage';
import { DeckBuilderPage } from './pages/DeckBuilderPage';
import { PlayPage } from './pages/PlayPage';
import { PrintPage } from './pages/PrintPage';
import { AddCardPage } from './pages/AddCardPage';
import { EditCardPage } from './pages/EditCardPage';
import { SequencingPage } from './pages/SequencingPage';
import { SequencingLibraryPage } from './pages/SequencingLibraryPage';
import { SequencingBuilderPage } from './pages/SequencingBuilderPage';
import { SequencingGeneratorPage } from './pages/SequencingGeneratorPage';
import { MatchingLibraryPage } from './pages/MatchingLibraryPage';
import { MatchingPage } from './pages/MatchingPage';
import { MatchingGeneratorPage } from './pages/MatchingGeneratorPage';
import { MatchingReviewPage } from './pages/MatchingReviewPage';
import { MatchingManagerPage } from './pages/MatchingManagerPage';
import { MatchingGenerateFromDeckPage } from './pages/MatchingGenerateFromDeckPage';
import { MatchingBuilderPage } from './pages/MatchingBuilderPage';
import { CategoriesSetupPage } from './pages/CategoriesSetupPage';
import { CategoriesPage } from './pages/CategoriesPage';
import { MathLibraryPage } from './pages/MathLibraryPage';
import { MathPracticePage } from './pages/MathPracticePage';
import { ActivityReviewPage } from './pages/ActivityReviewPage';
import { CurriculumManagerPage } from './pages/CurriculumManagerPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { SessionExpiredModal } from './components/SessionExpiredModal';

// "/" — landing page for guests, /dashboard redirect for logged-in users.
// Passes ?trialExpired=true through to LandingPage after a trial expires.
function SmartHome() {
  const { isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const trialExpired = searchParams.get('trialExpired') === 'true';

  if (loading) return <LoadingSpinner />;

  if (isAuthenticated) {
    // Trial expiration lockout disabled for now — see ProtectedRoute.js.
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage trialExpired={trialExpired} />;
}

// "/login" — only for guests; redirect logged-in users straight to the dashboard.
function PublicOnlyRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
}

function App() {
  return (
    <>
      <SessionExpiredModal />
      <Routes>
        <Route path="/" element={<SmartHome />} />
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

      <Route path="/library" element={<ProtectedRoute><DeckLibraryPage /></ProtectedRoute>} />
      <Route path="/create-set" element={<ProtectedRoute><DeckBuilderPage /></ProtectedRoute>} />
      <Route path="/create-deck" element={<Navigate to="/create-set" replace />} />
      <Route path="/sessions" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
      <Route path="/session/:deckName" element={<ProtectedRoute><SessionPage /></ProtectedRoute>} />
      <Route path="/deck/:deckName/add-card" element={<ProtectedRoute><AddCardPage /></ProtectedRoute>} />
      <Route path="/deck/:deckName/edit-card/:cardId" element={<ProtectedRoute><EditCardPage /></ProtectedRoute>} />
      <Route path="/data" element={<ProtectedRoute><DataPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/profiles" element={<ProtectedRoute><ProfilesPage /></ProtectedRoute>} />
      <Route path="/account" element={<Navigate to="/settings" replace />} />
      <Route path="/saved-decks" element={<ProtectedRoute><SavedDecksPage /></ProtectedRoute>} />
      <Route path="/quick-learn" element={<ProtectedRoute><QuickLearnPage /></ProtectedRoute>} />
      <Route path="/play" element={<ProtectedRoute><PlayPage /></ProtectedRoute>} />
      <Route path="/print" element={<ProtectedRoute><PrintPage /></ProtectedRoute>} />
      <Route path="/sequencing" element={<ProtectedRoute><SequencingLibraryPage /></ProtectedRoute>} />
      <Route path="/sequencing/create" element={<ProtectedRoute><SequencingBuilderPage /></ProtectedRoute>} />
      <Route path="/sequencing/generated/:deckId" element={<ProtectedRoute><SequencingPage generated /></ProtectedRoute>} />
      <Route path="/sequencing/user/:deckId" element={<ProtectedRoute><SequencingPage userSource /></ProtectedRoute>} />
      <Route path="/sequencing/:deckId" element={<ProtectedRoute><SequencingPage /></ProtectedRoute>} />

      <Route path="/matching" element={<ProtectedRoute><MatchingLibraryPage /></ProtectedRoute>} />
      <Route path="/matching/create" element={<ProtectedRoute><MatchingBuilderPage /></ProtectedRoute>} />
      <Route path="/matching/generated/:deckId" element={<ProtectedRoute><MatchingPage generated /></ProtectedRoute>} />
      <Route path="/matching/user/:deckId" element={<ProtectedRoute><MatchingPage userSource /></ProtectedRoute>} />
      <Route path="/matching/:deckId" element={<ProtectedRoute><MatchingPage /></ProtectedRoute>} />

      <Route path="/categories" element={<ProtectedRoute><CategoriesSetupPage /></ProtectedRoute>} />
      <Route path="/categories/play" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />

      <Route path="/math" element={<ProtectedRoute><MathLibraryPage /></ProtectedRoute>} />
      <Route path="/math/practice" element={<ProtectedRoute><MathPracticePage /></ProtectedRoute>} />

      {/* Admin-only: manual activity creation workflow, never exposed to customers */}
      <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
      <Route path="/admin/curriculum" element={<AdminRoute><CurriculumManagerPage /></AdminRoute>} />
      <Route path="/admin/matching" element={<AdminRoute><MatchingManagerPage /></AdminRoute>} />
      <Route path="/sequencing/generate" element={<AdminRoute><SequencingGeneratorPage /></AdminRoute>} />
      <Route path="/sequencing/review/:deckId" element={<AdminRoute><ActivityReviewPage /></AdminRoute>} />
      <Route path="/matching/generate" element={<AdminRoute><MatchingGeneratorPage /></AdminRoute>} />
      <Route path="/matching/generate-from-deck" element={<AdminRoute><MatchingGenerateFromDeckPage /></AdminRoute>} />
      <Route path="/matching/review/:deckId" element={<AdminRoute><MatchingReviewPage /></AdminRoute>} />

      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
